import {
  ALLOWED_RUNTIME_MATERIAL_CLASSES,
  MAX_GLB_SIZE_BYTES,
  REJECTED_GLB_EXTENSIONS,
  SUPPORTED_REQUIRED_GLB_EXTENSIONS,
  SUPPORTED_USED_GLB_EXTENSIONS,
} from '../../constants/uploads';
import { analyzeAnimations } from './analyzeAnimations';
import { analyzeMaterials } from './analyzeMaterials';
import { analyzeSceneSummary } from './analyzeSceneSummary';
import { analyzeTextures } from './analyzeTextures';
import type {
  GlbAnalysisIssue,
  GlbValidationReport,
  ParsedGlbBinary,
  ParsedRuntimeGlb,
} from './types';

export function validateSceneTopology(sceneCount: number): GlbAnalysisIssue[] {
  const issues: GlbAnalysisIssue[] = [];

  if (sceneCount === 0) {
    issues.push({
      code: 'scene.missing',
      severity: 'error',
      message: 'Validation failed: the GLB contains no scene.',
    });
  }

  if (sceneCount !== 1) {
    issues.push({
      code: 'scene.count',
      severity: 'error',
      message: `Exactly one scene is required for the cinematic/manual pipeline, found ${sceneCount}.`,
    });
  }

  return issues;
}

export function validateCameraAnimation(
  animatedCameraClipNames: string[],
  animatedCameraNodeNames: string[],
): GlbAnalysisIssue[] {
  const issues: GlbAnalysisIssue[] = [];

  if (animatedCameraClipNames.length !== 1) {
    issues.push({
      code: 'camera.animation.count',
      severity: 'error',
      message: `Exactly one camera animation clip is required for the cinematic/manual pipeline, found ${animatedCameraClipNames.length}.`,
    });
  }

  if (animatedCameraNodeNames.length !== 1) {
    issues.push({
      code: 'camera.node.count',
      severity: 'error',
      message: `Exactly one animated camera node is required for the cinematic/manual pipeline, found ${animatedCameraNodeNames.length}.`,
    });
  }

  return issues;
}

export function validateRequiredExtensions(
  requiredExtensions: string[] | undefined,
  supportedExtensions: Set<string>,
): GlbAnalysisIssue[] {
  const issues: GlbAnalysisIssue[] = [];

  for (const extension of requiredExtensions ?? []) {
    if (!supportedExtensions.has(extension)) {
      issues.push({
        code: 'extension.unsupported',
        severity: 'error',
        message: `Unsupported required glTF extension: ${extension}.`,
      });
    }
  }

  return issues;
}

export function validateUsedExtensions(
  usedExtensions: string[] | undefined,
  supportedExtensions: Set<string>,
  rejectedExtensions: Set<string>,
): GlbAnalysisIssue[] {
  const issues: GlbAnalysisIssue[] = [];

  for (const extension of usedExtensions ?? []) {
    if (rejectedExtensions.has(extension) || !supportedExtensions.has(extension)) {
      issues.push({
        code: 'extension.used.rejected',
        severity: 'error',
        message: `Unsupported glTF extension used by this pipeline asset: ${extension}.`,
      });
    }
  }

  return issues;
}

function hashString(input: string): string {
  let hash = 2166136261;

  for (let index = 0; index < input.length; index += 1) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }

  return `glb-${(hash >>> 0).toString(16)}`;
}

export function validateGlbAnalysis(params: {
  file: File;
  binary: ParsedGlbBinary;
  runtime: ParsedRuntimeGlb;
}): GlbValidationReport {
  const { file, binary, runtime } = params;

  const animationAnalysis = analyzeAnimations(runtime);
  const materialFindings = analyzeMaterials(binary, runtime, {
    allowedRuntimeMaterialClasses: ALLOWED_RUNTIME_MATERIAL_CLASSES,
    rejectedExtensions: REJECTED_GLB_EXTENSIONS,
    allowAlphaMask: true,
  });
  const textureIssues = analyzeTextures(binary, runtime);
  const issues: GlbAnalysisIssue[] = [
    ...validateSceneTopology(binary.sceneCount),
    ...validateRequiredExtensions(binary.requiredExtensions, SUPPORTED_REQUIRED_GLB_EXTENSIONS),
    ...validateUsedExtensions(
      binary.usedExtensions,
      SUPPORTED_USED_GLB_EXTENSIONS,
      REJECTED_GLB_EXTENSIONS,
    ),
    ...validateCameraAnimation(
      animationAnalysis.animatedCameraClipNames,
      animationAnalysis.animatedCameraNodeNames,
    ),
    ...materialFindings.flatMap((material) => material.issues),
    ...animationAnalysis.issues,
    ...textureIssues,
  ];

  const summary = analyzeSceneSummary(
    file,
    binary,
    runtime,
    animationAnalysis.animatedCameraClipNames.length,
    animationAnalysis.animatedCameraNodeNames.length,
    materialFindings,
  );

  const status = issues.some((issue) => issue.severity === 'error') ? 'fail' : 'pass';

  return {
    status,
    canLoadIntoScene: status === 'pass',
    fingerprint: hashString(
      JSON.stringify({
        name: file.name,
        size: file.size,
        modified: file.lastModified,
        scenes: summary.sceneCount,
        clips: summary.animationClipCount,
      }),
    ),
    summary,
    issues,
  };
}

export function validateSelectedFile(file: File): void {
  if (!file.name.toLowerCase().endsWith('.glb')) {
    throw new Error('Pipeline ingest must use the .glb extension.');
  }

  if (file.size === 0) {
    throw new Error('The selected GLB is empty.');
  }

  if (file.size > MAX_GLB_SIZE_BYTES) {
    throw new Error('The selected GLB exceeds the supported size limit.');
  }
}
